/**
 * 45R Variant Picker - Event-driven component (Quick Add modal)
 * Dispatches 'r45:variant:update' events for parent components to handle.
 *
 * NOTE: This class is only used by the Quick Add modal (45R-quick-add-modal.liquid).
 * The product page variant picker now uses Horizon's <variant-picker> custom element
 * directly (see snippets/45R-variant-picker.liquid).
 */
(function() {
  'use strict';

  if (window.R45VariantPicker) return;

  class R45VariantPicker {
    constructor(container) {
      this.container = container;
      this.pickerId = container.dataset.pickerId;
      this.productId = container.dataset.productId;
      this.context = container.dataset.context || '';

      const variantDataEl = container.querySelector('[data-product-variants]');
      this.variants = variantDataEl ? JSON.parse(variantDataEl.textContent) : [];

      const translationsEl = container.querySelector('[data-r45-translations]');
      this.t = translationsEl ? JSON.parse(translationsEl.textContent) : {};

      this.selectedColorEl = container.querySelector('[data-selected-color]');
      this.selectedSizeEl = container.querySelector('[data-selected-size]');
      this.selectedDisplayEl = container.querySelector('[data-selected-variant-display]');
      this.selectedStatusEl = container.querySelector('[data-selected-status]');

      this.selectedOptions = {};
      this.hasColorOption = container.querySelector('.r45-variant-picker__color') !== null;
      this.hasSizeOption = container.querySelector('.r45-variant-picker__size') !== null;
      this.colorSelected = false;
      this.sizeSelected = false;

      this.init();
    }

    init() {
      this.bindEvents();
      this.initializePreSelectedState();
      this.updateSelectedDisplayVisibility();
    }

    /**
     * Check for pre-selected options on page load
     * and initialize state accordingly
     */
    initializePreSelectedState() {
      // Check for pre-selected color
      const selectedColorInput = this.container.querySelector('.r45-color-swatch__input:checked');
      if (selectedColorInput) {
        const optionIndex = selectedColorInput.dataset.optionIndex;
        const value = selectedColorInput.value;
        this.selectedOptions[optionIndex] = value;
        this.colorSelected = true;
        this.updateSelectedColorText(value);
        this.updateSizeAvailability();
      }

      // Check for pre-selected size
      const selectedSizeInput = this.container.querySelector('.r45-size-button__input:checked');
      if (selectedSizeInput) {
        const optionIndex = selectedSizeInput.dataset.optionIndex;
        const value = selectedSizeInput.value;
        this.selectedOptions[optionIndex] = value;
        this.sizeSelected = true;
        this.updateSelectedSizeText(value);
        this.updateStatusDisplay();
      }
    }

    bindEvents() {
      // Color inputs
      this.container.querySelectorAll('.r45-color-swatch__input').forEach(input => {
        input.addEventListener('change', (e) => this.handleColorChange(e));
      });

      // Size inputs
      this.container.querySelectorAll('.r45-size-button__input').forEach(input => {
        input.addEventListener('change', (e) => this.handleSizeChange(e));
      });

      // Generic option inputs
      this.container.querySelectorAll('.r45-option-button__input').forEach(input => {
        input.addEventListener('change', (e) => this.handleOptionChange(e));
      });
    }

    handleColorChange(e) {
      const input = e.target;
      const optionIndex = input.dataset.optionIndex;
      const value = input.value;

      this.selectedOptions[optionIndex] = value;
      this.colorSelected = true;
      this.updateColorSwatchSelection(input);
      this.updateSelectedColorText(value);
      this.updateSizeAvailability();
      this.updateSelectedDisplayVisibility();
      this.dispatchVariantUpdate();
    }

    handleSizeChange(e) {
      const input = e.target;
      const optionIndex = input.dataset.optionIndex;
      const value = input.value;

      this.selectedOptions[optionIndex] = value;
      this.sizeSelected = true;
      this.updateSizeButtonSelection(input);
      this.updateSelectedSizeText(value);
      this.updateSelectedDisplayVisibility();
      this.updateStatusDisplay();
      this.dispatchVariantUpdate();
    }

    handleOptionChange(e) {
      const input = e.target;
      const optionIndex = input.dataset.optionIndex;
      const value = input.value;

      this.selectedOptions[optionIndex] = value;
      this.updateOptionButtonSelection(input);
      this.dispatchVariantUpdate();
    }

    updateColorSwatchSelection(selectedInput) {
      const wrapper = this.container.querySelector('.r45-variant-picker__color-swatches');
      if (!wrapper) return;

      wrapper.querySelectorAll('.r45-color-swatch').forEach(swatch => {
        swatch.classList.remove('is-selected');
      });

      const label = selectedInput.nextElementSibling;
      if (label) {
        label.classList.add('is-selected');
      }
    }

    updateSizeButtonSelection(selectedInput) {
      const wrapper = this.container.querySelector('.r45-variant-picker__size-buttons');
      if (!wrapper) return;

      const label = selectedInput.nextElementSibling;
      const wasAlreadySelected = label && label.classList.contains('is-selected');

      wrapper.querySelectorAll('.r45-size-button').forEach(btn => {
        btn.classList.remove('is-selected');
      });

      if (label) {
        label.classList.add('is-selected');
        if (!wasAlreadySelected) {
          const gif = label.querySelector('.circle-gif');
          if (gif) {
            const src = gif.src;
            gif.src = '';
            gif.src = src;
          }
        }
      }
    }

    updateOptionButtonSelection(selectedInput) {
      const wrapper = selectedInput.closest('.r45-variant-picker__buttons');
      if (!wrapper) return;
      wrapper.querySelectorAll('.r45-option-button').forEach(btn => {
        btn.classList.remove('is-selected');
      });
      const label = selectedInput.nextElementSibling;
      if (label) label.classList.add('is-selected');
    }

    stripColorPrefix(colorValue) {
      if (colorValue && colorValue.includes(' - ')) {
        return colorValue.split(' - ').pop();
      }
      return colorValue;
    }

    updateSelectedColorText(colorValue) {
      if (this.selectedColorEl) {
        this.selectedColorEl.textContent = this.stripColorPrefix(colorValue);
      }
    }

    updateSelectedSizeText(sizeValue) {
      if (this.selectedSizeEl) {
        this.selectedSizeEl.textContent = sizeValue;
      }
    }

    updateSelectedDisplayVisibility() {
      if (!this.selectedDisplayEl) return;
      const bothSelected = this.colorSelected && this.sizeSelected;
      if (bothSelected || this.colorSoldOut) {
        this.selectedDisplayEl.classList.remove('is-hidden');
      } else {
        this.selectedDisplayEl.classList.add('is-hidden');
      }
    }

    updateSizeAvailability() {
      const selectedColor = this.selectedOptions['0'];
      const sizeInputs = this.container.querySelectorAll('.r45-size-button__input');

      if (!selectedColor) {
        sizeInputs.forEach(input => {
          const wrapper = input.closest('.r45-size-button-wrapper');
          wrapper?.classList.remove('is-hidden');
        });
        return;
      }

      let allSizesUnavailable = true;

      sizeInputs.forEach(input => {
        const sizeValue = input.value;
        const wrapper = input.closest('.r45-size-button-wrapper');
        const label = input.nextElementSibling;

        const variantExists = this.variants.some(variant => {
          return variant.option1 === selectedColor && variant.option2 === sizeValue;
        });

        if (!variantExists) {
          wrapper?.classList.add('is-hidden');
          if (input.checked) {
            input.checked = false;
            label?.classList.remove('is-selected');
            delete this.selectedOptions[input.dataset.optionIndex];
            this.sizeSelected = false;
          }
        } else {
          wrapper?.classList.remove('is-hidden');

          const isAvailable = this.variants.some(variant => {
            return variant.option1 === selectedColor &&
                   variant.option2 === sizeValue &&
                   variant.available;
          });

          input.disabled = false;

          if (isAvailable) {
            label?.classList.remove('is-unavailable');
            allSizesUnavailable = false;
          } else {
            label?.classList.add('is-unavailable');
          }
        }
      });

      this.colorSoldOut = allSizesUnavailable;
      this.updateStatusDisplay();

      // Ensure display visibility is updated immediately when colorSoldOut changes
      if (this.colorSoldOut) {
        this.updateSelectedDisplayVisibility();
      }
    }

    updateStatusDisplay() {
      if (!this.selectedStatusEl) return;

      const variant = this.findVariant();
      const soldOutText = this.selectedDisplayEl?.getAttribute('data-sold-out-text') || this.t.soldOut || 'Sold Out';
      const lastOneText = this.selectedDisplayEl?.getAttribute('data-last-one-text') || this.t.lastOne || 'Last One';

      if (this.colorSoldOut) {
        this.selectedStatusEl.textContent = soldOutText;
        return;
      }

      if (variant) {
        if (!variant.available) {
          this.selectedStatusEl.textContent = soldOutText;
        } else if (variant.inventory_quantity === 1 && variant.inventory_management) {
          this.selectedStatusEl.textContent = lastOneText;
        } else {
          this.selectedStatusEl.textContent = '';
        }
      } else {
        this.selectedStatusEl.textContent = '';
      }
    }

    findVariant() {
      const optionValues = Object.keys(this.selectedOptions)
        .sort()
        .map(key => this.selectedOptions[key]);

      return this.variants.find(variant => {
        const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return optionValues.length === variantOptions.length &&
               optionValues.every((value, index) => value === variantOptions[index]);
      });
    }

    dispatchVariantUpdate() {
      const variant = this.findVariant();
      const selectedColor = this.selectedOptions['0'];

      // Dispatch event for parent components to handle
      const event = new CustomEvent('r45:variant:update', {
        bubbles: true,
        detail: {
          pickerId: this.pickerId,
          productId: this.productId,
          context: this.context,
          variant: variant,
          selectedOptions: { ...this.selectedOptions },
          colorSelected: this.colorSelected,
          sizeSelected: this.sizeSelected,
          colorSoldOut: this.colorSoldOut,
          selectedColor: selectedColor,
          translations: this.t
        }
      });

      this.container.dispatchEvent(event);
    }
  }

  window.R45VariantPicker = R45VariantPicker;

  function initR45VariantPickers() {
    document.querySelectorAll('[data-r45-variant-picker]').forEach(container => {
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        new R45VariantPicker(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initR45VariantPickers);
  } else {
    initR45VariantPickers();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initR45VariantPickers);
  }
})();